import React, { useState, useRef, useEffect } from 'react';
import Image from '../../../components/AppImage';
import Icon from '../../../components/AppIcon';

const BeforeAfterSlider = ({ beforeImage, afterImage, title }) => {
  const [sliderPosition, setSliderPosition] = useState(50);
  const [isDragging, setIsDragging] = useState(false);
  const containerRef = useRef(null);

  const handleMouseDown = (e) => {
    setIsDragging(true);
    updateSliderPosition(e);
  };

  const handleMouseMove = (e) => {
    if (!isDragging) return;
    updateSliderPosition(e);
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const handleTouchStart = (e) => {
    setIsDragging(true);
    updateSliderPosition(e?.touches?.[0]);
  };

  const handleTouchMove = (e) => {
    if (!isDragging) return;
    e?.preventDefault();
    updateSliderPosition(e?.touches?.[0]);
  };

  const handleTouchEnd = () => {
    setIsDragging(false);
  };

  const updateSliderPosition = (event) => {
    if (!containerRef?.current) return;
    
    const rect = containerRef?.current?.getBoundingClientRect();
    const x = event?.clientX - rect?.left;
    const percentage = Math.max(0, Math.min(100, (x / rect?.width) * 100));
    setSliderPosition(percentage);
  };

  React.useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.addEventListener('touchmove', handleTouchMove, { passive: false });
      document.addEventListener('touchend', handleTouchEnd);
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.removeEventListener('touchmove', handleTouchMove);
      document.removeEventListener('touchend', handleTouchEnd);
    };
  }, [isDragging]);

  return (
    <div className="bg-card rounded-2xl overflow-hidden shadow-lg">
      {/* Header */}
      <div className="p-4 border-b border-border">
        <h3 className="font-playfair font-bold text-lg text-text-primary">
          {title}
        </h3>
        <p className="text-text-secondary text-sm">
          Arraste o controle para ver a transformação
        </p>
      </div>

      {/* Before/After Container */}
      <div 
        ref={containerRef}
        className="relative h-80 overflow-hidden cursor-ew-resize select-none"
        onMouseDown={handleMouseDown}
        onTouchStart={handleTouchStart}
      >
        {/* After Image (Background) */}
        <div className="absolute inset-0">
          <Image
            src={afterImage}
            alt="Depois da transformação"
            className="w-full h-full object-cover"
          />
          <div className="absolute bottom-4 right-4 px-3 py-1 bg-success text-success-foreground text-sm font-semibold rounded-full">
            Depois
          </div>
        </div>

        {/* Before Image (Clipped) */}
        <div 
          className="absolute inset-0 overflow-hidden"
          style={{ clipPath: `inset(0 ${100 - sliderPosition}% 0 0)` }}
        >
          <Image
            src={beforeImage}
            alt="Antes da transformação"
            className="w-full h-full object-cover"
          />
          <div className="absolute bottom-4 left-4 px-3 py-1 bg-warning text-warning-foreground text-sm font-semibold rounded-full">
            Antes
          </div>
        </div>

        {/* Slider Line */}
        <div 
          className="absolute top-0 bottom-0 w-1 bg-white shadow-lg cursor-ew-resize"
          style={{ left: `${sliderPosition}%`, transform: 'translateX(-50%)' }}
        >
          {/* Slider Handle */}
          <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-8 h-8 bg-white rounded-full shadow-lg flex items-center justify-center cursor-ew-resize">
            <div className="flex space-x-1">
              <div className="w-0.5 h-4 bg-text-secondary rounded-full" />
              <div className="w-0.5 h-4 bg-text-secondary rounded-full" />
            </div>
          </div>
        </div>

        {/* Drag Instructions */}
        {!isDragging && sliderPosition === 50 && (
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <div className="bg-black/70 text-white px-4 py-2 rounded-lg flex items-center space-x-2 animate-pulse">
              <Icon name="Move" size={16} color="white" />
              <span className="text-sm">Arraste para comparar</span>
            </div>
          </div>
        )}
      </div>

      {/* Controls */}
      <div className="p-4 bg-muted">
        <div className="flex items-center justify-between">
          <button
            onClick={() => setSliderPosition(0)}
            className="flex items-center space-x-2 px-3 py-1 bg-warning text-warning-foreground rounded-lg text-sm font-medium hover:bg-warning/80 transition-colors duration-200"
          >
            <Icon name="RotateCcw" size={14} />
            <span>Ver Antes</span>
          </button>
          
          <div className="flex items-center space-x-2 text-text-secondary text-sm">
            <Icon name="Eye" size={14} />
            <span>{Math.round(sliderPosition)}% transformado</span>
          </div>
          
          <button
            onClick={() => setSliderPosition(100)}
            className="flex items-center space-x-2 px-3 py-1 bg-success text-success-foreground rounded-lg text-sm font-medium hover:bg-success/80 transition-colors duration-200"
          >
            <span>Ver Depois</span>
            <Icon name="RotateCw" size={14} />
          </button>
        </div>
      </div>
    </div>
  );
};

export default BeforeAfterSlider;